import get from 'lodash-es/get';
import some from 'lodash-es/some';
import firestoreRedux from '@dreamworld/firestore-redux';

import * as auth from '../auth';
import * as feature from '../feature';
import * as router from '../router';
import { CARD_SCOPED_COLLECTIONS } from './utils.js';

/**
 * A document reading these still shows what the author wrote: `PENDING` has not been reached by
 * translation yet, `IN_PROGRESS` is out with the Translator. Everything else - success, partial
 * failure, failure - is as translated as it is ever going to get.
 */
const WAITING_STATUSES = ['PENDING', 'IN_PROGRESS'];

/**
 * @param {Object} state Redux state.
 * @returns {Boolean} `true` when the card's translate button is shown: the account has the
 * `translate-card` feature, a user is signed in, and the session is not impersonated. Impersonation is excluded because
 * the library's language is app-wide, so a card pick would re-translate the whole impersonated session.
 */
export const canTranslateCard = (state) =>
  feature.selectors.isAvailable(state, 'translate-card', router.selectors.accountId(state)) &&
  !!auth.selectors.currentUserId(state) &&
  !auth.selectors.impersonatedUser(state);

/**
 * @param {Object} state Redux state.
 * @param {String} cardId
 * @returns {String|undefined} The language this card is showing, or `undefined` while it shows the
 * original.
 */
export const translatedLanguage = (state, cardId) => get(state, `card-translate.cards.${cardId}.language`);

/**
 * @param {Object} state Redux state.
 * @returns {String|null} The language picked last on this device, for the top of the menu, or
 * `null` when nothing has been translated here yet.
 */
export const lastLanguage = (state) => get(state, 'card-translate.lastLanguage') || null;

/**
 * @param {Object} state Redux state.
 * @param {String} cardId
 * @returns {Boolean} `true` while any of the card's documents is still on its source text. A
 * primitive, so it is referentially stable without memoizing, and it costs nothing at all on a
 * card that is not translated.
 */
export const isArriving = (state, cardId) => {
  if (!translatedLanguage(state, cardId)) {
    return false;
  }

  if (WAITING_STATUSES.includes(firestoreRedux.selectors.translation.status(state, 'cards', cardId))) {
    return true;
  }

  return some(CARD_SCOPED_COLLECTIONS, (collection) =>
    some(
      firestoreRedux.selectors.allDocs(state, collection),
      (doc) => doc?.cardId === cardId && WAITING_STATUSES.includes(firestoreRedux.selectors.translation.status(state, collection, doc.id))
    )
  );
};

/**
 * @param {Object} state Redux state.
 * @param {String} cardId
 * @returns {Boolean} `true` when the card is marked translated but the Translator gave up on its
 * own `cards` document.
 */
export const isFailed = (state, cardId) =>
  !!translatedLanguage(state, cardId) && firestoreRedux.selectors.translation.status(state, 'cards', cardId) === 'FAILED';
